import { IconButton, Tooltip, useTheme } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import MedAgDataGrid from '../../../../components/MedAgDataGrid'
import { ColDef, INumberFilterParams } from 'ag-grid-community'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { styles } from './LabDevices'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getAllLabDeviceModels,
  getDeviceModelById,
  getTestCodesByModelId,
  uploadTestCodeFile,
} from '../../../../services/LmsService'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsFormMode,
  setLmsFormMode,
  setLmsItemId,
} from '../../lmsSlice'
import {
  ADD,
  EDIT,
  lmsFormCreateMode,
  lmsFormEditMode,
  lmsFormNoneMode,
  MODEL,
  parameterMapping,
  parameterMappingItem,
} from '../../constants'
import CreateUpdateParameterMapping from './CreateUpdateParameterMapping'
import KeycloakService from '../../../../utils/keycloakService'
import { SUPER_ADMIN_DELETE } from '../../../../utils/roles'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import { toast } from 'react-toastify'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { readableDateTimeFormat } from '../../../../utils/dateUtils'
import CustomHeader from '../../../Radiology/components/RadiologyModel/CustomHeader'
import { deviceModel } from './CreateUpdateLabDevice'

const ParametersMapping = () => {
  const depTableContainerRef = useRef<HTMLDivElement | null>(null)
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const navigate = useNavigate()
  const { id } = useParams()
  const [data, setData] = useState<any>([])
  const [model, setModel] = useState<any>(null)
  const mode = useAppSelector(selectedLmsFormMode)
  const dispatch = useAppDispatch()
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [parameterDeleteItem, setParameterDeleteItem] = useState<any>({})
  const [selectItem, setSelectItem] = useState<any>({})
  const importFile = useRef<HTMLInputElement | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const [models, setModels] = useState<deviceModel[]>([])

  function handleImportClick() {
    if (importFile?.current) {
      importFile?.current?.click()
    }
  }

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event?.target?.files?.[0]
    if (file) {
      const formData = new FormData()
      formData?.append('file', file)
      const response = await uploadTestCodeFile(formData, model?.id)
      if (response?.success) {
        toast?.success(response?.message)
        getDeviceParametersById(model?.id, page, pageSize)
      } else {
        toast?.error(response?.message)
      }
    }
    event.target.value = ''
  }

  function handleBackClick() {
    if (mode !== lmsFormNoneMode) {
      dispatch(setLmsFormMode(lmsFormNoneMode))
    } else {
      navigate(-1)
    }
  }

  async function onEdit(id: any) {
    dispatch(setLmsItemId(id))
    dispatch(setLmsFormMode(lmsFormEditMode))
  }

  function handleCreateButton() {
    if (selectItem?.id) {
      onEdit(selectItem?.id)
    } else {
      dispatch(setLmsFormMode(lmsFormCreateMode))
    }
  }

  const handleDeleteDialogOpen = (data: any) => {
    setDeleteDialog(true)
    setParameterDeleteItem(data)
  }

  function handleCloseDeleteDialog() {
    setDeleteDialog(false)
    setParameterDeleteItem({})
  }

  const actionColumn = (params: any) => {
    return (
      <div>
        <IconButton
          title='Delete'
          size='small'
          onClick={() => handleDeleteDialogOpen(params?.data)}
          disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
        >
          <DeleteOutlined fontSize='small' />
        </IconButton>
      </div>
    )
  }

  function handleCheckBox(data: any) {
    if (data?.id === selectItem?.id) {
      setSelectItem({})
    } else {
      setSelectItem(data)
    }
  }

  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'checkbox',
      minWidth: 50,
      sortable: false,
      filter: false,
      headerComponent: () => {
        return <input className='LisHeaderCheckboxStyle' type='checkbox' />
      },
      checkboxSelection: false,
      cellRenderer: (params: any) => {
        return (
          <input
            className='LisRowCheckboxStyle'
            type='checkbox'
            onChange={() => handleCheckBox(params?.data)}
            checked={params?.data?.id == selectItem?.id}
          />
        )
      },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Id',
      field: 'id',
      minWidth: 80,
      sortable: true,
      filter: false,
      headerClass: 'custom-header-cell',
      cellClass: classes?.columnFontSize,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Channel Id',
      field: 'channel_id',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Device Parameter Code',
      field: 'test_code',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Test Type',
      field: 'test_type',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Device Parameter Name',
      field: 'test_name',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'THOP Parameter Code',
      field: 'thop_parameter_code',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 230,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'THOP Parameter Name',
      field: 'thop_name',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Result Type',
      field: 'result_type',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Decimal Precision',
      field: 'decimal_precision',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Scaling Factor',
      field: 'scaling_factor',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 170,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Created By',
      field: 'created_by',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Created On',
      field: 'created_at',
      sortable: true,
      filter: 'agDateColumnFilter',
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: (params: any) => {
        return params?.data?.created_at
          ? readableDateTimeFormat(new Date(params?.data?.created_at))
          : ''
      },
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Updated By',
      field: 'updated_by',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Updated On',
      field: 'updated_at',
      sortable: true,
      filter: 'agDateColumnFilter',
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: (params: any) => {
        return params?.data?.updated_at
          ? readableDateTimeFormat(new Date(params?.data?.updated_at))
          : ''
      },
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Action',
      sortable: true,
      filter: false,
      cellClass: classes?.enableColumnStyle,
      headerClass: 'custom-header-cell',
      cellRenderer: actionColumn,
      width: 100,
    },
  ]

  useEffect(() => {
    const resizeHandler = () => {
      const container = depTableContainerRef?.current
      if (container) {
        const availableHeight: any =
          window?.innerHeight - container?.getBoundingClientRect()?.top
        container.style.maxHeight = `${availableHeight - 20}px`
      }
    }

    window?.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window?.removeEventListener('resize', resizeHandler)
    }
  }, [])

  const getDeviceParametersById = useCallback(
    async (id: any, page: number, pageSize: number) => {
      const model = await getDeviceModelById(id)
      if (!model?.success) {
        toast?.error(model?.message)
      } else {
        setModel(model?.data)
      }
      const response = await getTestCodesByModelId(
        id,
        page * pageSize,
        pageSize
      )
      if (response?.success) {
        setData(response?.data?.data)
        setTotalCount(response?.data?.total_records)
      } else {
        toast?.error(response?.message)
      }
    },
    []
  )

  const getAllModels = useCallback(
    async (page: number, pageSize: number) => {
      const modelsData = await getAllLabDeviceModels(0, -1)
      if (modelsData?.success) {
        const modelsObj = modelsData?.data?.data?.map((item: any) => {
          return {
            name: item?.name,
            mode: item?.default_connection_type,
            id: item?.id,
          }
        })
        setModels(modelsObj)
        setModel(modelsObj?.[0])
        getDeviceParametersById(modelsObj?.[0]?.id, page, pageSize)
      } else {
        toast?.error(modelsData?.message)
      }
    },
    [getDeviceParametersById]
  )

  useEffect(() => {
    if (!id && mode === lmsFormNoneMode) {
      getAllModels(page, pageSize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, getAllModels])

  useEffect(() => {
    if (id && mode === lmsFormNoneMode) {
      getDeviceParametersById(id, page, pageSize)
    } else if (model && mode === lmsFormNoneMode) {
      getDeviceParametersById(model?.id, page, pageSize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode, getDeviceParametersById, page, pageSize])

  return (
    <div className='LisFirstDivStyle'>
      <div className='LisSecondDivStyle'>
        <div className='admin-header-text LisThirdDivStyle'>
          <div>
            <>
              <IconButton onClick={handleBackClick}>
                <KeyboardBackspaceIcon className='back-btn-lab' />
              </IconButton>
            </>
          </div>
          <span>
            {parameterMapping}&nbsp; : &nbsp;{model?.name}&nbsp;{MODEL}
          </span>
        </div>
        <div className='selectStyle'>
          {!id && mode === lmsFormNoneMode && (
            <select
              onChange={(event) =>
                getDeviceParametersById(event?.target?.value, page, pageSize)
              }
            >
              <option>{model?.name}</option>
              {models?.map((item: any) => (
                <option key={crypto?.randomUUID()} value={item?.id}>
                  {item?.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {mode === lmsFormNoneMode && (
          <div className='LisSearchCreateButton'>
            <div className='parameterMappingImportStyle'>
              <IconButton onClick={handleImportClick}>
                <Tooltip title='Import Parameter Mappings'>
                  <FileDownloadIcon />
                </Tooltip>
              </IconButton>
              <form className='fileImportStyle'>
                <input
                  type='file'
                  ref={importFile}
                  onChange={handleFileUpload}
                  aria-labelledby='fileUpload'
                />
              </form>
            </div>
            <input
              className='admin-lab-searchbox LisSearchStyle'
              type='search'
              placeholder='Search'
            />
            <button className='admin-btn' onClick={handleCreateButton}>
              {selectItem?.id ? EDIT : ADD}
            </button>
          </div>
        )}
      </div>
      {mode === lmsFormNoneMode ? (
        <div className='LisAgGridStyle' ref={depTableContainerRef}>
          <MedAgDataGrid
            tableHeight={'98%'}
            rowData={data ? data : []}
            columnDefs={columnDefs}
            pagination={true}
            headerHeight={30}
            searchFieldWidth={200}
            searchField={false}
            exportIcon={false}
            pageSizeOptions={[10, 20, 50, 100]}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRows={totalCount}
          />
          {deleteDialog && (
            <DeleteConfirmationDialog
              openDialog={deleteDialog}
              closeDialog={handleCloseDeleteDialog}
              data={parameterDeleteItem}
              name={parameterDeleteItem?.test_code}
              setData={setData}
              item={parameterMappingItem}
            />
          )}
        </div>
      ) : (
        <CreateUpdateParameterMapping model={model} />
      )}
    </div>
  )
}

export default ParametersMapping
